import { Candidate, HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

export interface CandidateTableAction {
	loc: string;
	route?: string[];
	routeOptions?: {
		fromRoot?: boolean;
	};
	filter?: (candidate: Partial<Candidate>) => boolean;
}

export interface CandidateTableActions {
	primary: CandidateTableAction;
	secondary?: CandidateTableAction;
}

export interface CandidateSearchResultsTableLoc {
	fieldCmcId: string;
	fieldLogin: string;
	fieldFirstName: string;
	fieldLastName: string;
	fieldEmail: string;
	fieldCompanyName: string;
	fieldCity: string;
	fieldState: string;
	fieldCountry: string;
	fieldUnique?: string;
	fieldAffiliationEmail: string;
}

export type SPCandidateSearchLocs =
	| 'SPCandidateSearchCityLabel'
	| 'SPCandidateSearchCountryLabel'
	| 'SPCandidateSearchStateLabel'
	| 'SPCandidateSearchCompanyLabel'
	| 'SPCandidateSearchEmailLabel'
	| 'SPCandidateSearchNameFirstLabel'
	| 'SPCandidateSearchNameLastLabel'
	| 'SPCandidateSearchCmcIdLabel'
	| 'SPCandidateSearchExternalIdLabel'
	| 'SPCandidateSearchUniqueLabel'
	| 'SPCandidateSearchAffiliationEmailLabel';

export const spCandidateSearchDefaultLocs: LocDefs<SPCandidateSearchLocs> = {
	SPCandidateSearchCityLabel: {
		defaultValue: 'City',
		description: 'Column heading for: city.',
		legacyName: 'CandidateProfile.Addresses.Primary/Candidate.List.ColumnHeading.AddressCity',
		displayPath: 'Candidates|List|City',
		displayType: 'Label',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
	SPCandidateSearchCountryLabel: {
		defaultValue: 'Country',
		description: 'Column heading for: country.',
		legacyName:
			'CandidateProfile.Addresses.Primary/Candidate.List.ColumnHeading.AddressCountry',
		displayPath: 'Candidates|List|Country',
		displayType: 'Label',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
	SPCandidateSearchStateLabel: {
		defaultValue: 'State',
		description: 'Column heading for: state.',
		legacyName: 'CandidateProfile.Addresses.Primary/Candidate.List.ColumnHeading.AddressState',
		displayPath: 'Candidates|List|State',
		displayType: 'Label',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
	SPCandidateSearchCompanyLabel: {
		defaultValue: 'Company',
		description: 'Column heading for: company.',
		legacyName: 'CandidateProfile.PrimaryInformation/Candidate.List.ColumnHeading.CompanyName',
		displayPath: 'Candidates|List|Company',
		displayType: 'Label',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
	SPCandidateSearchEmailLabel: {
		defaultValue: 'Email',
		description: 'Column heading for: email.',
		legacyName: 'CandidateProfile.PrimaryInformation/Candidate.List.ColumnHeading.Email',
		displayPath: 'Candidates|List|Email',
		displayType: 'Label',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
	SPCandidateSearchAffiliationEmailLabel: {
		defaultValue: 'Affiliation Email',
		description: 'Column heading for: affiliation email.',
		displayPath: 'Candidates|List|Affiliation Email',
		displayType: 'Label',
		feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
	SPCandidateSearchNameFirstLabel: {
		defaultValue: 'First Name',
		description: 'Column heading for: first name.',
		legacyName: 'CandidateProfile.PrimaryInformation/Candidate.List.ColumnHeading.NameFirst',
		displayPath: 'Candidates|List|First Name',
		displayType: 'Label',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
	SPCandidateSearchNameLastLabel: {
		defaultValue: 'Last Name',
		description: 'Column heading for: last name.',
		legacyName: 'CandidateProfile.PrimaryInformation/Candidate.List.ColumnHeading.NameLast',
		displayPath: 'Candidates|List|Last Name',
		displayType: 'Label',
		feature: HubFeature.CandidateProfilePrimaryInformation,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
	SPCandidateSearchCmcIdLabel: {
		defaultValue: 'CMC ID',
		description: 'Column heading for: CMC ID.',
		legacyName: 'Core/Candidate.List.ColumnHeading.CmcId',
		displayPath: 'Candidates|List|CMC ID',
		displayType: 'Label',
		feature: HubFeature.Core,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
	SPCandidateSearchExternalIdLabel: {
		defaultValue: 'Login',
		description: 'Column heading for: login.',
		legacyName: 'Core/Candidate.List.ColumnHeading.ExternalId',
		displayPath: 'Candidates|List|Login',
		displayType: 'Label',
		feature: HubFeature.Core,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
	SPCandidateSearchUniqueLabel: {
		defaultValue: 'Unique',
		description: 'Column heading for Unique.',
		legacyName: 'MergeCandidates/Candidate.List.ColumnHeading.Unique',
		displayPath: 'Candidates|List|Unique',
		displayType: 'Label',
		feature: HubFeature.MergeCandidates,
		htmlAllowed: false,
		shared: true,
		portal: 'SP',
	},
};

export type SPCandidateSearchLocKeys = LocMap<SPCandidateSearchLocs>;

export const spCandidateSearchLocKeys = MapLocKeys<SPCandidateSearchLocs>(
	spCandidateSearchDefaultLocs
);

export const mapCandidateSearchDefaultLocs = (
	locs: SPCandidateSearchLocKeys
): CandidateSearchResultsTableLoc => ({
	fieldCmcId: locs.SPCandidateSearchCmcIdLabel,
	fieldLogin: locs.SPCandidateSearchExternalIdLabel,
	fieldFirstName: locs.SPCandidateSearchNameFirstLabel,
	fieldLastName: locs.SPCandidateSearchNameLastLabel,
	fieldEmail: locs.SPCandidateSearchEmailLabel,
	fieldCompanyName: locs.SPCandidateSearchCompanyLabel,
	fieldCity: locs.SPCandidateSearchCityLabel,
	fieldState: locs.SPCandidateSearchStateLabel,
	fieldCountry: locs.SPCandidateSearchCountryLabel,
	fieldUnique: locs.SPCandidateSearchUniqueLabel,
	fieldAffiliationEmail: locs.SPCandidateSearchAffiliationEmailLabel,
});
